'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Image as ImageIcon, Check, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import { useOutputStyles } from '@/shared/api/hooks/use-output-styles';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { cn } from '@/shared/lib/cn';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { ProBadge } from '@/features/subscription';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';
import { ProjectReferenceSheet } from '../scrollable-settings/ProjectReferenceSheet';
import { ThumbStrip } from '../scrollable-settings/ReferenceSummary';
import { useProjectReferences } from '@/features/project-reference/hooks/use-project-references';

export interface OutputContentProps {
  selectedOutputStyleId: number | null;
  onOutputStyleChange: (styleId: number | null) => void;
  // Layout variant — kept for API parity; both variants share the Apple
  // single-column preset-card idiom now.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
  // Project reference props (optional)
  projectId?: string;
  outputProjectReferenceIds?: string[] | null;
  onOutputProjectReferencesChange?: (ids: string[] | null) => void;
  onNavigateToReferences?: () => void;
  /** `has_advanced_render_controls` — TRUE for Pro Pass holders and unlimited
   *  buildings. When FALSE, the non-photorealistic styles are locked. */
  advancedUsable?: boolean;
}

export function OutputContent({
  selectedOutputStyleId,
  onOutputStyleChange,
  closeOnSelect,
  projectId,
  outputProjectReferenceIds,
  onOutputProjectReferencesChange,
  onNavigateToReferences,
  advancedUsable = true,
}: OutputContentProps) {
  const t = useTranslations('render.settings.output');
  // "From reference" row label + the picker panel reuse the shared strings.
  const tRef = useTranslations('render.settings.projectReference');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const showLimitPaywall = useLimitPaywallStore((s) => s.showPaywall);
  const showPackPaywall = useLimitPaywallStore((s) => s.showPackPaywall);
  // Server-driven "unlock this Pro feature" payload (the Facade Kit unlocks
  // it) — the shared advanced-controls payload. Non-photo output
  // gates on the same is_advanced_render_controls flag as Hi-Res / model /
  // effort, so it shares the code.
  const proFeaturePaywall = usePaywallPayload('PRO_FEATURE_UNAVAILABLE');
  const openLockedPaywall = () => {
    // Until the cached plan payload has loaded (rare — the plan is fetched well
    // before the user reaches this control) open the Kit sheet directly; it
    // loads its own copy from GET /store. Parity with Apple's
    // `showFeaturePaywall` and Android's `FeaturePaywallViewModel` fallback — a
    // locked knob must never be a dead tap.
    if (proFeaturePaywall) {
      showLimitPaywall(proFeaturePaywall, 'PRO_FEATURE_UNAVAILABLE');
    } else {
      showPackPaywall();
    }
  };
  // Photorealistic is the ONLY free render style. Per the 2026-07 "non-photo
  // styles + Atlas are advanced controls" gating decision, the custom "From
  // reference" style and the artistic presets are ADVANCED: when the building
  // can't use advanced controls (`!advancedUsable`) they show a PRO badge and a
  // tap opens the Pro feature chooser instead of applying the style. The picker
  // itself stays browsable for everyone (the row always opens). Mirrors
  // Apple/Android.
  const { data: outputStyles = [], isLoading } = useOutputStyles();

  // Resolve the selected output reference's thumbnail for the row accessory.
  const { data: allRefs = [] } = useProjectReferences(projectId || '', {
    enabled: !!projectId,
  });
  const selectedRefImages = (outputProjectReferenceIds || [])
    .map((id) => allRefs.find((r) => r.id === id)?.attributes.file_url)
    .filter((url): url is string => !!url);

  const hasSelectedRefs = (outputProjectReferenceIds?.length ?? 0) > 0;
  // Photorealistic is selected when no style AND no refs
  const isPhotorealistic = selectedOutputStyleId === null && !hasSelectedRefs;
  // From reference is selected when refs are chosen
  const isFromReference = hasSelectedRefs;

  const handleSelect = (styleId: number | null) => {
    if (hasSelectedRefs) {
      onOutputProjectReferencesChange?.(null);
    }
    onOutputStyleChange(styleId);
    closeOnSelect?.();
  };

  const handleRefsSelectionChange = (ids: string[]) => {
    if (ids.length > 0) {
      onOutputStyleChange(null);
    }
    onOutputProjectReferencesChange?.(ids.length > 0 ? ids : null);
  };

  return (
    <div className="space-y-4">
      {/* Photorealistic — Apple parity: bordered pill row (xl corner radius,
          accent stroke when active). */}
      <button
        type="button"
        className={cn(
          'w-full flex items-center gap-3 py-3 px-4 rounded-2xl text-start',
          'transition-colors',
          isPhotorealistic
            ? 'border-2 border-primary'
            : 'border border-border/60'
        )}
        onClick={() => handleSelect(null)}
      >
        <div className="w-11 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
          <ImageIcon
            className={cn(
              'w-4 h-4',
              isPhotorealistic ? 'text-primary' : 'text-muted-foreground'
            )}
          />
        </div>
        <span className="flex-1 text-settings-row-title text-foreground">
          {t('photorealistic')}
        </span>
        {isPhotorealistic && (
          <Check
            className="w-5 h-5 text-primary flex-shrink-0"
            strokeWidth={2.5}
          />
        )}
      </button>

      {/* Custom style from references — a "From reference" drill-down to the
          single-select picker panel (grid + inline upload). Mirrors the aspect
          axes (Style/Materials/Colors). */}
      {projectId && onOutputProjectReferencesChange && (
        <>
          <SettingsCard>
            <PickerRow
              title={tRef('fromReference')}
              isSelected={!advancedUsable ? false : isFromReference}
              trailing={!advancedUsable ? <ProBadge /> : undefined}
              // Locked (non-advanced building) → open the Pro feature chooser
              // instead of drilling into the reference picker.
              onClick={
                advancedUsable
                  ? () => setIsSheetOpen(true)
                  : openLockedPaywall
              }
              accessory={
                advancedUsable ? (
                  <div className="flex items-center gap-2">
                    {selectedRefImages.length > 0 && (
                      <ThumbStrip images={selectedRefImages} shape="circle" />
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
                  </div>
                ) : undefined
              }
            />
          </SettingsCard>

          <ProjectReferenceSheet
            projectId={projectId}
            category="output"
            variant="output"
            selectedIds={outputProjectReferenceIds || []}
            onSelectionChange={handleRefsSelectionChange}
            maxSelection={1}
            open={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            onNavigateToReferences={onNavigateToReferences}
          />
        </>
      )}

      {/* Artistic style preset cards — full-bleed preview + name + checkmark.
          Mirrors Apple `OutputStylePresetCard`: small leading thumbnails made
          every preset look identical on phones; full-bleed previews let users
          actually distinguish styles. */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden border border-border/60"
            >
              <Skeleton className="w-full aspect-[168/120] rounded-none" />
              <div className="p-3">
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        outputStyles.length > 0 && (
          <div className="space-y-3">
            <p className="text-settings-row-value text-muted-foreground px-1">
              {t('artistic')}
            </p>
            {outputStyles.map((style) => {
              const isSelected =
                advancedUsable && selectedOutputStyleId === parseInt(style.id);

              return (
                <button
                  type="button"
                  key={style.id}
                  className={cn(
                    'block w-full text-start rounded-xl overflow-hidden transition-colors',
                    'bg-muted/40',
                    isSelected
                      ? 'border-2 border-primary'
                      : 'border border-border/60'
                  )}
                  // Locked artistic preset (non-advanced building) → open the
                  // Pro Pass paywall instead of applying the style.
                  onClick={
                    advancedUsable
                      ? () => handleSelect(isSelected ? null : parseInt(style.id))
                      : openLockedPaywall
                  }
                >
                  <div className="relative w-full aspect-[168/120] bg-muted overflow-hidden">
                    <Image
                      src={style.attributes.preview_image_url}
                      alt={style.attributes.name}
                      fill
                      sizes="(max-width: 480px) 100vw, 480px"
                      className="object-cover"
                    />
                  </div>
                  <div className="px-4 py-2.5 flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        'text-settings-row-title',
                        isSelected
                          ? 'text-primary font-semibold'
                          : 'text-foreground'
                      )}
                    >
                      {style.attributes.name}
                    </span>
                    {!advancedUsable ? (
                      <ProBadge />
                    ) : (
                      isSelected && (
                        <Check
                          className="w-5 h-5 text-primary flex-shrink-0"
                          strokeWidth={2.5}
                        />
                      )
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// Helper to get subtitle for preview
export function getOutputSubtitle(
  selectedOutputStyleId: number | null,
  outputStyles: { id: string; attributes: { name: string } }[],
  t: (key: string) => string,
  outputProjectReferenceIds?: string[] | null
): string {
  // The render style is ONE choice with two mutually exclusive forms: a preset
  // (outputStyleId) OR custom file-based references. A render generated from a
  // file reference has outputStyleId === null but non-empty references — without
  // this it falls back to "Photo" instead of "From reference" (matches Apple/Android).
  if (outputProjectReferenceIds && outputProjectReferenceIds.length > 0) {
    return t('fromReference');
  }
  if (selectedOutputStyleId === null) {
    return t('photorealistic');
  }
  const selectedStyle = outputStyles.find(
    (s) => parseInt(s.id) === selectedOutputStyleId
  );
  return selectedStyle?.attributes.name || t('photorealistic');
}
